import { Modal, Form, Input, InputNumber, Row, Col, Typography, Grid } from "antd";
import { SaveOutlined, CloseOutlined, CarOutlined, InfoCircleOutlined, DollarOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import type { FmvRecord } from "./FmvTableComponent";

const { Text } = Typography;

type FmvUpdateModalProps = {
  open: boolean;
  record: FmvRecord | null;
  onCancel: () => void;
  onSave: (updatedRecord: FmvRecord) => void;
  loading: boolean;
};

export const FmvUpdateModal = ({
  open,
  record,
  onCancel,
  onSave,
  loading,
}: FmvUpdateModalProps) => {
  const [form] = Form.useForm();
  const screens = Grid.useBreakpoint();

  useEffect(() => {
    if (record && open) {
      form.setFieldsValue(record);
    }
  }, [record, open, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSave({ ...record, ...values } as FmvRecord);
    } catch {
      // validation errors handled by antd
    }
  };

  const modalWidth = 720;

  const customHeader = (
    <div style={{
      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
      padding: '24px',
      margin: '-20px -24px 24px -24px',
      borderRadius: '8px 8px 0 0',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.15)',
        padding: '12px',
        borderRadius: '12px',
        display: 'flex',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(4px)'
      }}>
        <CarOutlined style={{ fontSize: '28px', color: '#fff' }} />
      </div>
      <div>
        <div style={{ fontSize: '20px', fontWeight: 600, letterSpacing: '-0.3px' }}>
          Update FMV Entry
        </div>
        <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '2px' }}>
          Update vehicle details and pricing information
        </div>
      </div>
    </div>
  );

  const cardStyle = {
    background: '#ffffff',
    border: '1px solid #d6dee8',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.03)'
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: '20px'
  };

  const priceFieldProps = {
    style: { width: "100%", borderRadius: '6px' },
    formatter: (v: any) => `₱ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
    parser: (v: any) => v!.replace(/₱\s?|(,*)/g, "") as any,
    min: 0,
  };

  return (
    <Modal
      title={customHeader}
      closable={false}
      open={open}
      onOk={handleOk}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      okText="Save Changes"
      cancelText="Cancel"
      confirmLoading={loading}
      centered
      width={screens.xs ? "100%" : modalWidth}
      style={screens.xs ? { top: 0, padding: 0 } : { paddingBottom: 0 }}
      bodyStyle={{ overflowY: "auto", overflowX: "hidden", maxHeight: screens.xs ? "calc(100vh - 120px)" : "calc(100vh - 220px)" }}
      okButtonProps={{
        icon: <SaveOutlined />,
        size: 'large',
        style: { backgroundColor: "#1e293b", borderColor: "#1e293b", borderRadius: "8px", fontWeight: 500, padding: '0 24px' },
      }}
      cancelButtonProps={{
        icon: <CloseOutlined />,
        size: 'large',
        style: { borderRadius: "8px", fontWeight: 500, padding: '0 24px' },
      }}
    >
      {record && (
        <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #334155' }}>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            Editing: <Text strong style={{ color: '#1e293b' }}>{record.BMTYTL}</Text>
          </Text>
        </div>
      )}

      <Form form={form} layout="vertical" size="large">
        <div style={cardStyle}>
          <div style={headerStyle}>
            <InfoCircleOutlined style={{ color: '#334155' }} />
            Vehicle Information
          </div>
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="brand" label={<span style={{ fontWeight: 500 }}>Brand</span>} rules={[{ required: true }]}>
                <Input style={{ borderRadius: '6px' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="model" label={<span style={{ fontWeight: 500 }}>Model</span>} rules={[{ required: true }]}>
                <Input style={{ borderRadius: '6px' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="trim" label={<span style={{ fontWeight: 500 }}>Trim</span>} rules={[{ required: true }]}>
                <Input style={{ borderRadius: '6px' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="year" label={<span style={{ fontWeight: 500 }}>Year</span>} rules={[{ required: true }]}>
                <InputNumber style={{ width: "100%", borderRadius: '6px' }} min={2000} max={2030} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="transmission" label={<span style={{ fontWeight: 500 }}>Transmission</span>} rules={[{ required: true }]}>
                <Input style={{ borderRadius: '6px' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="location" label={<span style={{ fontWeight: 500 }}>Location</span>} rules={[{ required: true }]}>
                <Input style={{ borderRadius: '6px' }} />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div style={cardStyle}>
          <div style={headerStyle}>
            <DollarOutlined style={{ color: '#334155' }} />
            Pricing Information
          </div>
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="discounted_cash_price" label={<span style={{ fontWeight: 500 }}>Discounted Cash Price</span>}>
                <InputNumber {...priceFieldProps} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="target_buying" label={<span style={{ fontWeight: 500 }}>Target Buying</span>}>
                <InputNumber {...priceFieldProps} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="gdfi_fmv" label={<span style={{ fontWeight: 500 }}>GDFI FMV</span>}>
                <InputNumber {...priceFieldProps} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="gdfi_fin_p" label={<span style={{ fontWeight: 500 }}>GDFI Fin Price</span>}>
                <InputNumber {...priceFieldProps} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="tfs_fin_p" label={<span style={{ fontWeight: 500 }}>TFS Fin Price</span>}>
                <InputNumber {...priceFieldProps} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="pre_order_cash_price" label={<span style={{ fontWeight: 500 }}>Pre-order Cash Price</span>}>
                <InputNumber {...priceFieldProps} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="reserve_fin_p" label={<span style={{ fontWeight: 500 }}>Reserve Fin Price</span>}>
                <InputNumber {...priceFieldProps} />
              </Form.Item>
            </Col>
          </Row>
        </div>
      </Form>
    </Modal>
  );
};
